import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation } from 'react-router-dom';
import {
  FaUser, FaLock, FaBuilding, FaCheck,
  FaExclamationCircle, FaCopy, FaEnvelope, FaPhone,
  FaIdBadge, FaShieldAlt, FaTrashAlt, FaExclamationTriangle,
  FaTimes, FaSpinner, FaEye, FaEyeSlash, FaCalendarAlt,
  FaCrown, FaSignOutAlt, FaExternalLinkAlt, FaLink, FaUnlink,
  FaToggleOn, FaToggleOff, FaGoogle, FaWindows
} from 'react-icons/fa';
import MainHeader from '../../shared/MainHeader/MainHeader';
import DigitalBadge from '../../compliance/DigitalBadge/DigitalBadge';
import { getApiEndpoint } from '../../../config/api';
import { clearToken } from '../../../utils/auth';
import './AccountSettings.css';

/* ── Inline alert ──────────────────────────────────────────── */
const Alert = ({ type, message, onDismiss }) => {
  if (!message) return null;
  return (
    <div className={`acct-alert acct-alert-${type}`} role={type === 'error' ? 'alert' : 'status'}>
      {type === 'success' ? <FaCheck className="acct-alert-icon" /> : <FaExclamationCircle className="acct-alert-icon" />}
      <span className="acct-alert-text">{message}</span>
      {onDismiss && (
        <button className="acct-alert-dismiss" onClick={onDismiss} aria-label="Dismiss">&times;</button>
      )}
    </div>
  );
};

/* ── Password field with toggle ────────────────────────────── */
const PasswordInput = ({ id, className, name, value, onChange, autoComplete, ariaDescribedBy, placeholder }) => {
  const [visible, setVisible] = useState(false);
  return (
    <div className="acct-pw-wrap">
      <input
        id={id}
        className={className}
        type={visible ? 'text' : 'password'}
        name={name}
        value={value}
        onChange={onChange}
        autoComplete={autoComplete}
        aria-describedby={ariaDescribedBy}
        placeholder={placeholder}
      />
      <button
        type="button"
        className="acct-pw-toggle"
        onClick={() => setVisible(v => !v)}
        aria-label={visible ? 'Hide password' : 'Show password'}
        tabIndex={-1}
      >
        {visible ? <FaEyeSlash /> : <FaEye />}
      </button>
    </div>
  );
};

/* ── Main component ────────────────────────────────────────── */
export default function AccountSettings() {
  const token = localStorage.getItem('token');

  /* Profile state */
  const [profile, setProfile] = useState({
    firstName: '', lastName: '', email: '', phoneNumber: '',
    role: '', companyName: '', companyCode: '', userCode: '',
    accountType: '', isCompanyAdmin: false, createdAt: '',
    internalStaffAccount: false, twoFactorEnabled: false,
  });
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);

  /* Profile save state */
  const [saving, setSaving] = useState(false);
  const [profileMsg, setProfileMsg] = useState({ type: '', text: '' });

  /* Password state */
  const [passwords, setPasswords] = useState({
    currentPassword: '', newPassword: '', confirmPassword: '',
  });
  const [changingPw, setChangingPw] = useState(false);
  const [pwMsg, setPwMsg] = useState({ type: '', text: '' });
  const [pwErrors, setPwErrors] = useState({});

  /* Two-factor authentication state */
  const [twoFactorSetup, setTwoFactorSetup] = useState(null);
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [twoFactorBackupCodes, setTwoFactorBackupCodes] = useState([]);
  const [twoFactorDisable, setTwoFactorDisable] = useState({ password: '', code: '' });
  const [twoFactorLoading, setTwoFactorLoading] = useState(false);
  const [twoFactorMsg, setTwoFactorMsg] = useState({ type: '', text: '' });

  /* Copy state */
  const [copied, setCopied] = useState(null);

  /* ID Badge state */
  const [showIdBadge, setShowIdBadge] = useState(false);

  /* ── Fetch profile ───────────────────────────────────────── */
  const fetchProfile = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(getApiEndpoint('/api/users/profile'), {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to load profile');
      const d = await res.json();
      setProfile({
        firstName: d.firstName ?? d.first_name ?? '',
        lastName: d.lastName ?? d.last_name ?? '',
        email: d.email ?? '',
        phoneNumber: d.phoneNumber ?? d.phone_number ?? '',
        role: d.role ?? '',
        companyName: d.companyName ?? d.company_name ?? '',
        companyCode: d.companyCode ?? d.company_code ?? '',
        userCode: d.userCode ?? d.user_code ?? '',
        accountType: d.accountType ?? d.account_type ?? '',
        isCompanyAdmin: d.isCompanyAdmin ?? d.is_company_admin ?? false,
        createdAt: d.createdAt ?? d.created_at ?? '',
        internalStaffAccount: !!d.internalStaffAccount,
        twoFactorEnabled: !!d.twoFactorEnabled,
      });
      setFetchError(null);
    } catch {
      setFetchError('Could not load your profile. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchProfile(); }, [fetchProfile]);

  /* ── Handlers ────────────────────────────────────────────── */
  const handleProfileField = (e) => {
    const { name, value } = e.target;
    setProfile(prev => ({ ...prev, [name]: value }));
    if (profileMsg.text) setProfileMsg({ type: '', text: '' });
  };

  const handlePasswordField = (e) => {
    const { name, value } = e.target;
    setPasswords(prev => ({ ...prev, [name]: value }));
    if (pwErrors[name]) setPwErrors(prev => ({ ...prev, [name]: '' }));
    if (pwMsg.text) setPwMsg({ type: '', text: '' });
  };

  const startTwoFactorSetup = async () => {
    setTwoFactorLoading(true);
    setTwoFactorMsg({ type: '', text: '' });
    setTwoFactorBackupCodes([]);
    try {
      const res = await fetch(getApiEndpoint('/api/auth/2fa/setup'), {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || 'Failed to start 2FA setup.');
      setTwoFactorSetup(data);
      setTwoFactorCode('');
    } catch (error) {
      setTwoFactorMsg({ type: 'error', text: error.message || 'Failed to start 2FA setup.' });
    } finally {
      setTwoFactorLoading(false);
    }
  };

  const verifyTwoFactorSetup = async (e) => {
    e.preventDefault();
    if (!twoFactorCode.trim()) {
      setTwoFactorMsg({ type: 'error', text: 'Enter the code from your authenticator app.' });
      return;
    }

    setTwoFactorLoading(true);
    setTwoFactorMsg({ type: '', text: '' });
    try {
      const res = await fetch(getApiEndpoint('/api/auth/2fa/verify-setup'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ code: twoFactorCode }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || 'Invalid verification code.');
      setProfile(prev => ({ ...prev, twoFactorEnabled: true }));
      setTwoFactorSetup(null);
      setTwoFactorCode('');
      setTwoFactorBackupCodes(data.backupCodes || []);
      setTwoFactorMsg({ type: 'success', text: 'Two-factor authentication is enabled.' });
    } catch (error) {
      setTwoFactorMsg({ type: 'error', text: error.message || 'Failed to verify 2FA setup.' });
    } finally {
      setTwoFactorLoading(false);
    }
  };

  const disableTwoFactor = async (e) => {
    e.preventDefault();
    if (!twoFactorDisable.password || !twoFactorDisable.code) {
      setTwoFactorMsg({ type: 'error', text: 'Enter your password and verification code.' });
      return;
    }

    setTwoFactorLoading(true);
    setTwoFactorMsg({ type: '', text: '' });
    try {
      const res = await fetch(getApiEndpoint('/api/auth/2fa/disable'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(twoFactorDisable),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || 'Failed to disable 2FA.');
      setProfile(prev => ({ ...prev, twoFactorEnabled: false }));
      setTwoFactorDisable({ password: '', code: '' });
      setTwoFactorBackupCodes([]);
      setTwoFactorSetup(null);
      setTwoFactorMsg({ type: 'success', text: 'Two-factor authentication is disabled.' });
    } catch (error) {
      setTwoFactorMsg({ type: 'error', text: error.message || 'Failed to disable 2FA.' });
    } finally {
      setTwoFactorLoading(false);
    }
  };

  /* ── Save profile ────────────────────────────────────────── */
  const saveProfile = async (e) => {
    e.preventDefault();
    setSaving(true);
    setProfileMsg({ type: '', text: '' });
    try {
      const res = await fetch(getApiEndpoint('/api/users/update-profile'), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          firstName: profile.firstName,
          lastName: profile.lastName,
          phoneNumber: profile.phoneNumber,
        }),
      });
      if (res.ok) {
        setProfileMsg({ type: 'success', text: 'Profile updated successfully.' });
      } else {
        const err = await res.json().catch(() => ({}));
        setProfileMsg({ type: 'error', text: err.message || 'Failed to update profile.' });
      }
    } catch {
      setProfileMsg({ type: 'error', text: 'Network error. Please try again.' });
    } finally {
      setSaving(false);
    }
  };

  /* ── Change password ─────────────────────────────────────── */
  const changePassword = async (e) => {
    e.preventDefault();
    const errors = {};
    if (!passwords.currentPassword) errors.currentPassword = 'Current password is required.';
    if (!passwords.newPassword) errors.newPassword = 'New password is required.';
    else if (passwords.newPassword.length < 8) errors.newPassword = 'Must be at least 8 characters.';
    if (!passwords.confirmPassword) errors.confirmPassword = 'Please confirm your new password.';
    else if (passwords.newPassword !== passwords.confirmPassword) errors.confirmPassword = 'Passwords do not match.';

    if (Object.keys(errors).length) {
      setPwErrors(errors);
      return;
    }

    setChangingPw(true);
    setPwMsg({ type: '', text: '' });
    setPwErrors({});
    try {
      const res = await fetch(getApiEndpoint('/api/users/change-password'), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          currentPassword: passwords.currentPassword,
          newPassword: passwords.newPassword,
        }),
      });
      if (res.ok) {
        setPwMsg({ type: 'success', text: 'Password changed successfully.' });
        setPasswords({ currentPassword: '', newPassword: '', confirmPassword: '' });
      } else {
        const err = await res.json().catch(() => ({}));
        setPwMsg({ type: 'error', text: err.message || 'Failed to change password.' });
      }
    } catch {
      setPwMsg({ type: 'error', text: 'Network error. Please try again.' });
    } finally {
      setChangingPw(false);
    }
  };

  /* ── Copy to clipboard ───────────────────────────────────── */
  const copyToClipboard = async (text, key) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(key);
      setTimeout(() => setCopied(null), 2000);
    } catch { /* silent */ }
  };

  /* ── Logout handler ──────────────────────────────────────── */
  const handleLogout = () => {
    clearToken();
    window.location.href = '/login';
  };

  /* ── Account deletion state ─────────────────────────────── */
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [deletionBlocked, setDeletionBlocked] = useState(null);
  const [checkingEligibility, setCheckingEligibility] = useState(false);
  const deleteInputRef = useRef(null);
  const modalRef = useRef(null);

  /* ── Calendar sync state ─────────────────────────────────── */
  const location = useLocation();
  const [calConnections, setCalConnections] = useState({});   // { google: {...}, microsoft: {...} }
  const [calReady, setCalReady] = useState({ google: false, microsoft: false });
  const [calLoading, setCalLoading] = useState(false);
  const [calMsg, setCalMsg] = useState({ type: '', text: '' });
  const [calPrefSaving, setCalPrefSaving] = useState({});

  const EVENT_TYPES = [
    { key: 'inspections', label: 'Inspections' },
    { key: 'jobs',        label: 'Jobs' },
    { key: 'tasks',       label: 'Tasks' },
    { key: 'shifts',      label: 'Shifts' },
  ];

  const fetchCalStatus = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(getApiEndpoint('/api/calendar-sync/status'), {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return;
      const d = await res.json();
      setCalConnections(d.connections || {});
      setCalReady({ google: !!d.googleReady, microsoft: !!d.microsoftReady });
    } catch { /* silent */ }
  }, [token]);

  useEffect(() => { fetchCalStatus(); }, [fetchCalStatus]);

  // Handle OAuth redirect back: ?calSync=success&provider=google
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const result = params.get('calSync');
    const provider = params.get('provider');
    if (!result) return;
    if (result === 'success') {
      setCalMsg({ type: 'success', text: `${provider === 'google' ? 'Google' : 'Microsoft'} Calendar connected successfully.` });
      fetchCalStatus();
    } else {
      setCalMsg({ type: 'error', text: `Could not connect ${provider === 'google' ? 'Google' : 'Microsoft'} Calendar. Please try again.` });
    }
    // Clean the URL so a refresh doesn't re-trigger
    const clean = window.location.pathname;
    window.history.replaceState({}, '', clean);
  }, [location.search, fetchCalStatus]);

  const connectCalendar = async (provider) => {
    setCalLoading(true);
    setCalMsg({ type: '', text: '' });
    try {
      const res = await fetch(getApiEndpoint(`/api/calendar-sync/connect/${provider}`), {
        headers: { Authorization: `Bearer ${token}` },
      });
      const d = await res.json();
      if (!res.ok) {
        setCalMsg({ type: 'error', text: d.message || 'Could not start connection.' });
        return;
      }
      window.location.href = d.redirectUrl;
    } catch {
      setCalMsg({ type: 'error', text: 'Network error. Please try again.' });
    } finally {
      setCalLoading(false);
    }
  };

  const disconnectCalendar = async (provider) => {
    setCalLoading(true);
    setCalMsg({ type: '', text: '' });
    try {
      const res = await fetch(getApiEndpoint(`/api/calendar-sync/disconnect/${provider}`), {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        setCalConnections(prev => {
          const next = { ...prev };
          delete next[provider];
          return next;
        });
        setCalMsg({ type: 'success', text: `${provider === 'google' ? 'Google' : 'Microsoft'} Calendar disconnected.` });
      } else {
        const d = await res.json().catch(() => ({}));
        setCalMsg({ type: 'error', text: d.message || 'Failed to disconnect.' });
      }
    } catch {
      setCalMsg({ type: 'error', text: 'Network error. Please try again.' });
    } finally {
      setCalLoading(false);
    }
  };

  const updateCalPref = async (provider, key, value) => {
    const current = calConnections[provider]?.syncEvents || {};
    const updated = { ...current, [key]: value };
    setCalConnections(prev => ({
      ...prev,
      [provider]: { ...prev[provider], syncEvents: updated },
    }));
    setCalPrefSaving(prev => ({ ...prev, [provider]: true }));
    try {
      await fetch(getApiEndpoint('/api/calendar-sync/preferences'), {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ provider, syncEvents: updated }),
      });
    } catch { /* silent — optimistic update stays */ }
    finally {
      setCalPrefSaving(prev => ({ ...prev, [provider]: false }));
    }
  };

  const isDeleteConfirmed =
    deleteConfirmText === 'DELETE' ||
    (profile.email && deleteConfirmText.toLowerCase() === profile.email.toLowerCase());

  const openDeleteModal = async () => {
    setDeleteError('');
    setDeleteConfirmText('');
    setDeletionBlocked(null);
    setCheckingEligibility(true);
    setShowDeleteModal(true);

    try {
      const res = await fetch(getApiEndpoint('/api/users/account/deletion-check'), {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!data.safe) {
        setDeletionBlocked(data.reason || 'Account cannot be deleted at this time.');
      }
    } catch {
      setDeletionBlocked('Unable to verify deletion eligibility. Please try again.');
    } finally {
      setCheckingEligibility(false);
    }
  };

  const closeDeleteModal = () => {
    if (deleting) return;
    setShowDeleteModal(false);
    setDeleteConfirmText('');
    setDeleteError('');
    setDeletionBlocked(null);
  };

  const handleDeleteAccount = async () => {
    if (!isDeleteConfirmed || deleting) return;
    setDeleting(true);
    setDeleteError('');

    try {
      const res = await fetch(getApiEndpoint('/api/users/account'), {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ confirmationText: deleteConfirmText }),
      });

      if (res.ok) {
        clearToken();
        window.location.href = '/login?deleted=1';
      } else {
        const err = await res.json().catch(() => ({}));
        setDeleteError(err.message || 'Failed to delete account. Please try again.');
      }
    } catch {
      setDeleteError('Network error. Please check your connection and try again.');
    } finally {
      setDeleting(false);
    }
  };

  // Focus trap & escape key for modal
  useEffect(() => {
    if (!showDeleteModal) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') closeDeleteModal();
      if (e.key === 'Tab' && modalRef.current) {
        const focusable = modalRef.current.querySelectorAll(
          'button, input, [tabindex]:not([tabindex="-1"])'
        );
        if (focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    setTimeout(() => deleteInputRef.current?.focus(), 100);
    return () => document.removeEventListener('keydown', handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showDeleteModal]);

  /* ── Derived ─────────────────────────────────────────────── */
  const hasCompany = !!(profile.companyName || profile.companyCode);
  const showBmId = profile.role === 'building_manager' && profile.userCode;
  const displayRole = (profile.role || '').replace(/[-_]/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  const initials = `${(profile.firstName || '')[0] || ''}${(profile.lastName || '')[0] || ''}`.toUpperCase() || '?';
  const displayAccountType = (profile.accountType || 'standard').replace(/[-_]/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

  const memberSince = profile.createdAt
    ? new Date(profile.createdAt).toLocaleDateString('en-AU', { month: 'short', year: 'numeric' })
    : null;

  /* ── Skeleton ────────────────────────────────────────────── */
  const Skeleton = () => (
    <div className="acct-skeleton" aria-hidden="true">
      <div className="acct-skel-block acct-skel-hero">
        <div className="acct-skel-avatar" />
        <div className="acct-skel-hero-text">
          <div className="acct-skel-line acct-skel-w40" />
          <div className="acct-skel-line acct-skel-w60" style={{ height: 10 }} />
        </div>
      </div>
      <div className="acct-skel-grid">
        <div className="acct-skel-block">
          <div className="acct-skel-line acct-skel-w40" />
          <div className="acct-skel-line acct-skel-w100" />
          <div className="acct-skel-line acct-skel-w100" />
          <div className="acct-skel-line acct-skel-w60" />
        </div>
        <div className="acct-skel-block">
          <div className="acct-skel-line acct-skel-w40" />
          <div className="acct-skel-line acct-skel-w100" />
          <div className="acct-skel-line acct-skel-w100" />
        </div>
      </div>
    </div>
  );

  /* ── ID Badge view ──────────────────────────────────────── */
  if (showIdBadge) {
    return <DigitalBadge onBack={() => setShowIdBadge(false)} />;
  }

  /* ── Render ──────────────────────────────────────────────── */
  return (
    <div className="acct-page">
      <MainHeader />

      <main className="acct-content">
        {/* Loading */}
        {loading && <Skeleton />}

        {/* Error */}
        {!loading && fetchError && (
          <div className="acct-error-card">
            <FaExclamationCircle className="acct-error-icon" />
            <h2 className="acct-error-title">Something went wrong</h2>
            <p className="acct-error-desc">{fetchError}</p>
            <button className="acct-btn-primary" onClick={() => { setLoading(true); setFetchError(null); fetchProfile(); }}>
              Retry
            </button>
          </div>
        )}

        {/* Content */}
        {!loading && !fetchError && (
          <>
            {/* ── Hero / Profile Header ── */}
            <div className="acct-hero">
              <div className="acct-avatar">{initials}</div>
              <div className="acct-hero-info">
                <h1 className="acct-hero-name">
                  {profile.firstName} {profile.lastName}
                </h1>
                <div className="acct-hero-meta">
                  <span className="acct-hero-email">{profile.email}</span>
                </div>
              </div>
              <div className="acct-hero-badges">
                {!profile.internalStaffAccount && profile.role && (
                <span className="acct-role-badge">
                  <FaShieldAlt className="acct-role-icon" />
                  {displayRole}
                </span>
                )}
                {profile.accountType && (
                  <span className="acct-type-badge">
                    <FaCrown className="acct-type-icon" />
                    {displayAccountType}
                  </span>
                )}
                {memberSince && (
                  <span className="acct-member-badge">
                    <FaCalendarAlt className="acct-member-icon" />
                    Member since {memberSince}
                  </span>
                )}
              </div>
              <button className="acct-logout-btn" onClick={handleLogout} title="Sign out">
                <FaSignOutAlt />
              </button>
            </div>

            {/* ── Two-column grid ── */}
            <div className="acct-grid">
              {/* Left column */}
              <div className="acct-col">
                {/* ── Personal Information ── */}
                <section className="acct-section">
                  <div className="acct-section-card">
                    <div className="acct-section-header">
                      <div className="acct-section-icon acct-icon-blue"><FaUser /></div>
                      <div>
                        <h2 className="acct-section-title">Personal Information</h2>
                        <p className="acct-section-desc">Update your name and contact details.</p>
                      </div>
                    </div>

                    <form className="acct-form" onSubmit={saveProfile}>
                      <Alert type={profileMsg.type} message={profileMsg.text} onDismiss={() => setProfileMsg({ type: '', text: '' })} />

                      <div className="acct-form-grid">
                        <div className="acct-field">
                          <label className="acct-label" htmlFor="acct-firstName">First Name</label>
                          <input
                            id="acct-firstName"
                            className="acct-input"
                            type="text"
                            name="firstName"
                            value={profile.firstName}
                            onChange={handleProfileField}
                            autoComplete="given-name"
                          />
                        </div>

                        <div className="acct-field">
                          <label className="acct-label" htmlFor="acct-lastName">Last Name</label>
                          <input
                            id="acct-lastName"
                            className="acct-input"
                            type="text"
                            name="lastName"
                            value={profile.lastName}
                            onChange={handleProfileField}
                            autoComplete="family-name"
                          />
                        </div>

                        <div className="acct-field">
                          <label className="acct-label" htmlFor="acct-email">
                            <FaEnvelope className="acct-label-icon" /> Email
                          </label>
                          <input
                            id="acct-email"
                            className="acct-input acct-input-readonly"
                            type="email"
                            value={profile.email}
                            readOnly
                            tabIndex={-1}
                          />
                          <span className="acct-field-hint">Contact support to update your email.</span>
                        </div>

                        <div className="acct-field">
                          <label className="acct-label" htmlFor="acct-phone">
                            <FaPhone className="acct-label-icon" /> Phone
                          </label>
                          <input
                            id="acct-phone"
                            className="acct-input"
                            type="tel"
                            name="phoneNumber"
                            value={profile.phoneNumber}
                            onChange={handleProfileField}
                            placeholder="e.g. +61 400 000 000"
                            autoComplete="tel"
                          />
                        </div>
                      </div>

                      {showBmId && (
                        <div className="acct-readonly-row" style={{ marginTop: 14 }}>
                          <div className="acct-readonly-label">
                            <FaIdBadge className="acct-label-icon" /> Building Manager ID
                          </div>
                          <div className="acct-code-chip">
                            <code>{profile.userCode}</code>
                            <button
                              type="button"
                              className={`acct-copy-btn ${copied === 'userCode' ? 'acct-copy-btn-done' : ''}`}
                              onClick={() => copyToClipboard(profile.userCode, 'userCode')}
                              aria-label="Copy Building Manager ID"
                            >
                              {copied === 'userCode' ? <FaCheck /> : <FaCopy />}
                            </button>
                          </div>
                        </div>
                      )}

                      <div className="acct-form-actions">
                        <button type="submit" className="acct-btn-primary" disabled={saving}>
                          {saving ? <><FaSpinner className="acct-spinner" /> Saving…</> : 'Save Changes'}
                        </button>
                      </div>
                    </form>
                  </div>
                </section>

                {/* ── CloudSafe ID ── */}
                {profile.userCode && !hasCompany && !showBmId && (
                  <section className="acct-section">
                    <div className="acct-section-card">
                      <div className="acct-section-header">
                        <div className="acct-section-icon acct-icon-purple"><FaIdBadge /></div>
                        <div>
                          <h2 className="acct-section-title">Your CloudSafe ID</h2>
                          <p className="acct-section-desc">Share this ID to get added to properties or companies.</p>
                        </div>
                      </div>
                      <div className="acct-company-grid">
                        <div className="acct-readonly-row">
                          <div className="acct-readonly-label">CloudSafe ID</div>
                          <div className="acct-code-chip">
                            <code>{profile.userCode}</code>
                            <button
                              type="button"
                              className={`acct-copy-btn ${copied === 'userCode' ? 'acct-copy-btn-done' : ''}`}
                              onClick={() => copyToClipboard(profile.userCode, 'userCode')}
                              aria-label="Copy CloudSafe ID"
                            >
                              {copied === 'userCode' ? <FaCheck /> : <FaCopy />}
                            </button>
                          </div>
                          <span className="acct-field-hint">Give this ID to a property owner or manager to get view access to a property.</span>
                        </div>
                        <div style={{ marginTop: 16 }}>
                          <button
                            type="button"
                            className="acct-btn-primary"
                            onClick={() => setShowIdBadge(true)}
                          >
                            <FaIdBadge style={{ marginRight: 6 }} /> View ID Badge
                          </button>
                        </div>
                      </div>
                    </div>
                  </section>
                )}

                {/* ── Company ── */}
                {hasCompany && (
                  <section className="acct-section">
                    <div className="acct-section-card">
                      <div className="acct-section-header">
                        <div className="acct-section-icon acct-icon-purple"><FaBuilding /></div>
                        <div>
                          <h2 className="acct-section-title">Company</h2>
                          <p className="acct-section-desc">Your organisation details.</p>
                        </div>
                      </div>

                      <div className="acct-company-grid">
                        {profile.companyName && (
                          <div className="acct-readonly-row">
                            <div className="acct-readonly-label">Company Name</div>
                            <div className="acct-readonly-value">{profile.companyName}</div>
                          </div>
                        )}

                        {profile.companyCode && (
                          <div className="acct-readonly-row">
                            <div className="acct-readonly-label">CloudSafe Company ID</div>
                            <div className="acct-code-chip">
                              <code>{profile.companyCode}</code>
                              <button
                                type="button"
                                className={`acct-copy-btn ${copied === 'companyCode' ? 'acct-copy-btn-done' : ''}`}
                                onClick={() => copyToClipboard(profile.companyCode, 'companyCode')}
                                aria-label="Copy Company ID"
                              >
                                {copied === 'companyCode' ? <FaCheck /> : <FaCopy />}
                              </button>
                            </div>
                            <span className="acct-field-hint">Share this ID with staff to link them to your company.</span>
                          </div>
                        )}

                        {profile.userCode && !showBmId && (
                          <div className="acct-readonly-row">
                            <div className="acct-readonly-label">Your User Code</div>
                            <div className="acct-code-chip">
                              <code>{profile.userCode}</code>
                              <button
                                type="button"
                                className={`acct-copy-btn ${copied === 'userCode' ? 'acct-copy-btn-done' : ''}`}
                                onClick={() => copyToClipboard(profile.userCode, 'userCode')}
                                aria-label="Copy User Code"
                              >
                                {copied === 'userCode' ? <FaCheck /> : <FaCopy />}
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </section>
                )}
              </div>

              {/* Right column */}
              <div className="acct-col">
                {/* ── Password ── */}
                <section className="acct-section">
                  <div className="acct-section-card">
                    <div className="acct-section-header">
                      <div className="acct-section-icon acct-icon-amber"><FaLock /></div>
                      <div>
                        <h2 className="acct-section-title">Change Password</h2>
                        <p className="acct-section-desc">Keep your account secure.</p>
                      </div>
                    </div>

                    <form className="acct-form" onSubmit={changePassword}>
                      <Alert type={pwMsg.type} message={pwMsg.text} onDismiss={() => setPwMsg({ type: '', text: '' })} />

                      <div className="acct-form-stack">
                        <div className="acct-field">
                          <label className="acct-label" htmlFor="acct-currentPw">Current Password</label>
                          <PasswordInput
                            id="acct-currentPw"
                            className={`acct-input ${pwErrors.currentPassword ? 'acct-input-error' : ''}`}
                            name="currentPassword"
                            value={passwords.currentPassword}
                            onChange={handlePasswordField}
                            autoComplete="current-password"
                            ariaDescribedBy={pwErrors.currentPassword ? 'acct-err-currentPw' : undefined}
                          />
                          {pwErrors.currentPassword && (
                            <span className="acct-field-error" id="acct-err-currentPw">{pwErrors.currentPassword}</span>
                          )}
                        </div>

                        <div className="acct-pw-divider" />

                        <div className="acct-field">
                          <label className="acct-label" htmlFor="acct-newPw">New Password</label>
                          <PasswordInput
                            id="acct-newPw"
                            className={`acct-input ${pwErrors.newPassword ? 'acct-input-error' : ''}`}
                            name="newPassword"
                            value={passwords.newPassword}
                            onChange={handlePasswordField}
                            autoComplete="new-password"
                            ariaDescribedBy={pwErrors.newPassword ? 'acct-err-newPw' : undefined}
                            placeholder="Min. 8 characters"
                          />
                          {pwErrors.newPassword && (
                            <span className="acct-field-error" id="acct-err-newPw">{pwErrors.newPassword}</span>
                          )}
                        </div>

                        <div className="acct-field">
                          <label className="acct-label" htmlFor="acct-confirmPw">Confirm New Password</label>
                          <PasswordInput
                            id="acct-confirmPw"
                            className={`acct-input ${pwErrors.confirmPassword ? 'acct-input-error' : ''}`}
                            name="confirmPassword"
                            value={passwords.confirmPassword}
                            onChange={handlePasswordField}
                            autoComplete="new-password"
                            ariaDescribedBy={pwErrors.confirmPassword ? 'acct-err-confirmPw' : undefined}
                          />
                          {pwErrors.confirmPassword && (
                            <span className="acct-field-error" id="acct-err-confirmPw">{pwErrors.confirmPassword}</span>
                          )}
                        </div>
                      </div>

                      <div className="acct-form-actions">
                        <button type="submit" className="acct-btn-primary" disabled={changingPw}>
                          {changingPw ? <><FaSpinner className="acct-spinner" /> Updating…</> : 'Update Password'}
                        </button>
                      </div>
                    </form>
                  </div>
                </section>

                {/* ── Two-Factor Authentication ── */}
                <section className="acct-section">
                  <div className="acct-section-card">
                    <div className="acct-section-header">
                      <div className="acct-section-icon acct-icon-blue"><FaShieldAlt /></div>
                      <div>
                        <h2 className="acct-section-title">Two-Factor Authentication</h2>
                        <p className="acct-section-desc">Use an authenticator app to protect your account.</p>
                      </div>
                    </div>

                    <div className="acct-form-stack">
                      <Alert type={twoFactorMsg.type} message={twoFactorMsg.text} onDismiss={() => setTwoFactorMsg({ type: '', text: '' })} />

                      <div className={`acct-2fa-status ${profile.twoFactorEnabled ? 'acct-2fa-on' : 'acct-2fa-off'}`}>
                        <FaShieldAlt />
                        <span>{profile.twoFactorEnabled ? '2FA is enabled' : '2FA is not enabled'}</span>
                      </div>

                      {!profile.twoFactorEnabled && !twoFactorSetup && twoFactorBackupCodes.length === 0 && (
                        <button
                          type="button"
                          className="acct-btn-primary"
                          onClick={startTwoFactorSetup}
                          disabled={twoFactorLoading}
                        >
                          {twoFactorLoading ? <><FaSpinner className="acct-spinner" /> Starting…</> : 'Enable 2FA'}
                        </button>
                      )}

                      {!profile.twoFactorEnabled && twoFactorSetup && (
                        <form className="acct-form-stack" onSubmit={verifyTwoFactorSetup}>
                          <div className="acct-2fa-qr-wrap">
                            <img src={twoFactorSetup.qrCodeDataUrl} alt="2FA setup QR code" className="acct-2fa-qr" />
                          </div>
                          <div className="acct-readonly-row">
                            <div className="acct-readonly-label">Manual setup key</div>
                            <div className="acct-code-chip">
                              <code>{twoFactorSetup.manualEntryKey}</code>
                              <button
                                type="button"
                                className={`acct-copy-btn ${copied === 'twoFactorKey' ? 'acct-copy-btn-done' : ''}`}
                                onClick={() => copyToClipboard(twoFactorSetup.manualEntryKey, 'twoFactorKey')}
                                aria-label="Copy setup key"
                              >
                                {copied === 'twoFactorKey' ? <FaCheck /> : <FaCopy />}
                              </button>
                            </div>
                          </div>
                          <div className="acct-field">
                            <label className="acct-label" htmlFor="acct-2fa-code">Authenticator code</label>
                            <input
                              id="acct-2fa-code"
                              className="acct-input"
                              type="text"
                              value={twoFactorCode}
                              onChange={(e) => setTwoFactorCode(e.target.value)}
                              inputMode="numeric"
                              autoComplete="one-time-code"
                              placeholder="123456"
                            />
                          </div>
                          <div className="acct-form-actions">
                            <button type="submit" className="acct-btn-primary" disabled={twoFactorLoading}>
                              {twoFactorLoading ? <><FaSpinner className="acct-spinner" /> Verifying…</> : 'Verify and Enable'}
                            </button>
                            <button
                              type="button"
                              className="acct-btn-secondary"
                              onClick={() => { setTwoFactorSetup(null); setTwoFactorCode(''); }}
                              disabled={twoFactorLoading}
                            >
                              Cancel
                            </button>
                          </div>
                        </form>
                      )}

                      {twoFactorBackupCodes.length > 0 && (
                        <div className="acct-backup-codes">
                          <div className="acct-readonly-label">Backup codes</div>
                          <p className="acct-field-hint">Store these somewhere safe. Each code can be used once.</p>
                          <div className="acct-backup-code-grid">
                            {twoFactorBackupCodes.map(code => <code key={code}>{code}</code>)}
                          </div>
                          <button
                            type="button"
                            className="acct-btn-secondary"
                            onClick={() => copyToClipboard(twoFactorBackupCodes.join('\n'), 'backupCodes')}
                          >
                            {copied === 'backupCodes' ? <FaCheck /> : <FaCopy />} Copy Codes
                          </button>
                        </div>
                      )}

                      {profile.twoFactorEnabled && (
                        <form className="acct-form-stack" onSubmit={disableTwoFactor}>
                          <div className="acct-field">
                            <label className="acct-label" htmlFor="acct-2fa-disable-password">Password</label>
                            <PasswordInput
                              id="acct-2fa-disable-password"
                              className="acct-input"
                              name="password"
                              value={twoFactorDisable.password}
                              onChange={(e) => setTwoFactorDisable(prev => ({ ...prev, password: e.target.value }))}
                              autoComplete="current-password"
                            />
                          </div>
                          <div className="acct-field">
                            <label className="acct-label" htmlFor="acct-2fa-disable-code">Authenticator or backup code</label>
                            <input
                              id="acct-2fa-disable-code"
                              className="acct-input"
                              type="text"
                              value={twoFactorDisable.code}
                              onChange={(e) => setTwoFactorDisable(prev => ({ ...prev, code: e.target.value }))}
                              autoComplete="one-time-code"
                              placeholder="123456"
                            />
                          </div>
                          <div className="acct-form-actions">
                            <button type="submit" className="acct-btn-danger" disabled={twoFactorLoading}>
                              {twoFactorLoading ? <><FaSpinner className="acct-spinner" /> Disabling…</> : 'Disable 2FA'}
                            </button>
                          </div>
                        </form>
                      )}
                    </div>
                  </div>
                </section>

                {/* ── Connections: Calendar ── */}
                <section className="acct-section">
                  <div className="acct-section-card">
                    <div className="acct-section-header">
                      <div className="acct-section-icon acct-icon-teal"><FaCalendarAlt /></div>
                      <div>
                        <h2 className="acct-section-title">Calendar</h2>
                        <p className="acct-section-desc">Sync CloudSafe events to your personal calendar.</p>
                      </div>
                    </div>

                    <div className="acct-form-stack">
                      <Alert type={calMsg.type} message={calMsg.text} onDismiss={() => setCalMsg({ type: '', text: '' })} />

                      <p className="acct-cal-privacy-note">
                        <FaShieldAlt className="acct-cal-privacy-icon" />
                        CloudSafe pushes your events to your calendar with full details. Existing events in your calendar are never read — they only appear as <strong>Blocked</strong> slots in CloudSafe.
                      </p>

                      {/* Google Calendar */}
                      {(() => {
                        const conn = calConnections['google'];
                        const ready = calReady.google;
                        return (
                          <div className="acct-cal-provider">
                            <div className="acct-cal-provider-header">
                              <div className="acct-cal-provider-logo acct-cal-google">
                                <FaGoogle />
                              </div>
                              <div className="acct-cal-provider-info">
                                <span className="acct-cal-provider-name">Google Calendar</span>
                                {conn?.connectedAt && (
                                  <span className="acct-cal-connected-since">
                                    Connected {new Date(conn.connectedAt).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' })}
                                  </span>
                                )}
                              </div>
                              <div className="acct-cal-provider-actions">
                                {!conn ? (
                                  <button
                                    className="acct-btn-primary acct-cal-connect-btn"
                                    onClick={() => connectCalendar('google')}
                                    disabled={calLoading || !ready}
                                    title={!ready ? 'Coming soon' : undefined}
                                  >
                                    {calLoading ? <FaSpinner className="acct-spinner" /> : <FaLink />}
                                    {ready ? 'Connect' : 'Coming soon'}
                                  </button>
                                ) : (
                                  <button
                                    className="acct-btn-secondary acct-cal-disconnect-btn"
                                    onClick={() => disconnectCalendar('google')}
                                    disabled={calLoading}
                                  >
                                    {calLoading ? <FaSpinner className="acct-spinner" /> : <FaUnlink />}
                                    Disconnect
                                  </button>
                                )}
                              </div>
                            </div>

                            {conn && (
                              <div className="acct-cal-prefs">
                                <p className="acct-cal-prefs-label">Sync these event types:</p>
                                <div className="acct-cal-toggles">
                                  {EVENT_TYPES.map(({ key, label }) => {
                                    const on = !!(conn.syncEvents?.[key]);
                                    return (
                                      <button
                                        key={key}
                                        className={`acct-cal-toggle-row ${on ? 'acct-cal-toggle-on' : ''}`}
                                        onClick={() => updateCalPref('google', key, !on)}
                                        disabled={calPrefSaving['google']}
                                      >
                                        <span className="acct-cal-toggle-label">{label}</span>
                                        {on ? <FaToggleOn className="acct-cal-toggle-icon acct-cal-toggle-icon-on" /> : <FaToggleOff className="acct-cal-toggle-icon" />}
                                      </button>
                                    );
                                  })}
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })()}

                      {/* Microsoft / Outlook */}
                      {(() => {
                        const conn = calConnections['microsoft'];
                        const ready = calReady.microsoft;
                        return (
                          <div className="acct-cal-provider">
                            <div className="acct-cal-provider-header">
                              <div className="acct-cal-provider-logo acct-cal-microsoft">
                                <FaWindows />
                              </div>
                              <div className="acct-cal-provider-info">
                                <span className="acct-cal-provider-name">Outlook / Teams</span>
                                {conn?.connectedAt && (
                                  <span className="acct-cal-connected-since">
                                    Connected {new Date(conn.connectedAt).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' })}
                                  </span>
                                )}
                              </div>
                              <div className="acct-cal-provider-actions">
                                {!conn ? (
                                  <button
                                    className="acct-btn-primary acct-cal-connect-btn"
                                    onClick={() => connectCalendar('microsoft')}
                                    disabled={calLoading || !ready}
                                    title={!ready ? 'Coming soon' : undefined}
                                  >
                                    {calLoading ? <FaSpinner className="acct-spinner" /> : <FaLink />}
                                    {ready ? 'Connect' : 'Coming soon'}
                                  </button>
                                ) : (
                                  <button
                                    className="acct-btn-secondary acct-cal-disconnect-btn"
                                    onClick={() => disconnectCalendar('microsoft')}
                                    disabled={calLoading}
                                  >
                                    {calLoading ? <FaSpinner className="acct-spinner" /> : <FaUnlink />}
                                    Disconnect
                                  </button>
                                )}
                              </div>
                            </div>

                            {conn && (
                              <div className="acct-cal-prefs">
                                <p className="acct-cal-prefs-label">Sync these event types:</p>
                                <div className="acct-cal-toggles">
                                  {EVENT_TYPES.map(({ key, label }) => {
                                    const on = !!(conn.syncEvents?.[key]);
                                    return (
                                      <button
                                        key={key}
                                        className={`acct-cal-toggle-row ${on ? 'acct-cal-toggle-on' : ''}`}
                                        onClick={() => updateCalPref('microsoft', key, !on)}
                                        disabled={calPrefSaving['microsoft']}
                                      >
                                        <span className="acct-cal-toggle-label">{label}</span>
                                        {on ? <FaToggleOn className="acct-cal-toggle-icon acct-cal-toggle-icon-on" /> : <FaToggleOff className="acct-cal-toggle-icon" />}
                                      </button>
                                    );
                                  })}
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                </section>

                {/* ── Danger Zone ── */}
                <section className="acct-section acct-danger-section">
                  <div className="acct-section-card acct-danger-card">
                    <div className="acct-section-header acct-danger-header">
                      <div className="acct-section-icon acct-icon-red"><FaExclamationTriangle /></div>
                      <div>
                        <h2 className="acct-section-title">Danger Zone</h2>
                        <p className="acct-section-desc">Irreversible account actions.</p>
                      </div>
                    </div>

                    <div className="acct-danger-body">
                      <div className="acct-danger-item">
                        <div className="acct-danger-info">
                          <h3 className="acct-danger-item-title">Delete Account</h3>
                          <p className="acct-danger-item-desc">
                            Permanently remove your account. Compliance records are preserved; personal data is anonymised.
                          </p>
                        </div>
                        <button
                          className="acct-btn-danger"
                          onClick={openDeleteModal}
                          type="button"
                        >
                          <FaTrashAlt className="acct-btn-icon" />
                          Delete
                        </button>
                      </div>

                      {profile.role === 'super_admin' && (
                        <div className="acct-danger-item">
                          <div className="acct-danger-info">
                            <h3 className="acct-danger-item-title">Cloud Staff portal</h3>
                            <p className="acct-danger-item-desc">
                              Internal tools, CRM, monitoring, and support workflows for CloudSafe operations.
                            </p>
                          </div>
                          <Link
                            to="/cloud-staff/dashboard"
                            className="acct-btn-primary acct-danger-portal-link"
                          >
                            <FaExternalLinkAlt className="acct-btn-icon" aria-hidden />
                            Open portal
                          </Link>
                        </div>
                      )}
                    </div>
                  </div>
                </section>
              </div>
            </div>

            {/* ── Delete Confirmation Modal ── */}
            {showDeleteModal && createPortal(
              <div className="acct-modal-overlay" role="dialog" aria-modal="true" aria-labelledby="acct-delete-title" onClick={(e) => { if (e.target === e.currentTarget) closeDeleteModal(); }}>
                <div className="acct-modal" ref={modalRef}>
                  <div className="acct-modal-header">
                    <div className="acct-modal-icon-wrap">
                      <FaExclamationTriangle className="acct-modal-icon" />
                    </div>
                    <h2 className="acct-modal-title" id="acct-delete-title">Delete Your Account</h2>
                    <button className="acct-modal-close" onClick={closeDeleteModal} aria-label="Close" disabled={deleting}>
                      <FaTimes />
                    </button>
                  </div>

                  <div className="acct-modal-body">
                    {checkingEligibility ? (
                      <div className="acct-modal-loading">
                        <FaSpinner className="acct-spinner" />
                        <span>Checking account status…</span>
                      </div>
                    ) : deletionBlocked ? (
                      <div className="acct-modal-blocked">
                        <Alert type="error" message={deletionBlocked} />
                        <p className="acct-modal-blocked-hint">
                          Please resolve the issue above before you can delete your account.
                        </p>
                      </div>
                    ) : (
                      <>
                        <div className="acct-modal-warning">
                          <p><strong>You have 30 days to recover your account.</strong></p>
                          <ul className="acct-modal-consequences">
                            <li>Your login access will be suspended immediately</li>
                            <li>You can contact CloudSafe support within 30 days to reinstate your account</li>
                            <li>After 30 days, your personal data will be permanently anonymised</li>
                            <li>Historical compliance records will be preserved for auditing</li>
                          </ul>
                        </div>

                        {deleteError && (
                          <Alert type="error" message={deleteError} onDismiss={() => setDeleteError('')} />
                        )}

                        <div className="acct-field acct-modal-confirm-field">
                          <label className="acct-label" htmlFor="acct-delete-confirm">
                            Type <strong>DELETE</strong> or your email to confirm
                          </label>
                          <input
                            ref={deleteInputRef}
                            id="acct-delete-confirm"
                            className="acct-input"
                            type="text"
                            value={deleteConfirmText}
                            onChange={(e) => setDeleteConfirmText(e.target.value)}
                            placeholder="Type DELETE to confirm"
                            autoComplete="off"
                            spellCheck="false"
                            disabled={deleting}
                          />
                        </div>
                      </>
                    )}
                  </div>

                  <div className="acct-modal-footer">
                    <button
                      className="acct-btn-secondary"
                      onClick={closeDeleteModal}
                      disabled={deleting}
                      type="button"
                    >
                      Cancel
                    </button>
                    {!deletionBlocked && !checkingEligibility && (
                      <button
                        className="acct-btn-danger acct-btn-danger-confirm"
                        onClick={handleDeleteAccount}
                        disabled={!isDeleteConfirmed || deleting}
                        type="button"
                      >
                        {deleting ? (
                          <>
                            <FaSpinner className="acct-spinner" />
                            Deleting…
                          </>
                        ) : (
                          <>
                            <FaTrashAlt className="acct-btn-icon" />
                            Permanently Delete Account
                          </>
                        )}
                      </button>
                    )}
                  </div>
                </div>
              </div>,
              document.body
            )}
          </>
        )}
      </main>
    </div>
  );
}
